import React, { useEffect, useState, useCallback } from 'react';
import {
  Folder, FileText, Upload, ChevronLeft, Loader2, ExternalLink,
  RefreshCw, Plus, FileSpreadsheet, Image as ImageIcon, Film
} from 'lucide-react';
import SpreadsheetEditor from './SpreadsheetEditor';

const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

const isSpreadsheetMime = (mime) => {
  if (!mime) return false;
  return (
    mime === 'application/vnd.google-apps.spreadsheet' ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'text/csv'
  );
};

const isSpreadsheetName = (name) => {
  if (!name) return false;
  const lower = name.toLowerCase();
  return lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xls');
};

const fileIcon = (mime, name) => {
  if (mime === 'application/vnd.google-apps.folder') return Folder;
  if (isSpreadsheetMime(mime) || isSpreadsheetName(name)) return FileSpreadsheet;
  if (mime?.startsWith('image/')) return ImageIcon;
  if (mime?.startsWith('video/')) return Film;
  return FileText;
};

const formatBytes = (n) => {
  if (!n) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const GoogleDrivePanel = ({ userId }) => {
  const [connected, setConnected] = useState(null); // null=loading, true/false
  const [folderStack, setFolderStack] = useState([{ id: 'root', name: 'My Drive' }]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showFolderInput, setShowFolderInput] = useState(false);

  // Spreadsheet editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorBytes, setEditorBytes] = useState(null);
  const [editorFile, setEditorFile] = useState(null);

  const currentFolder = folderStack[folderStack.length - 1];

  const checkStatus = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/status?user_id=${userId}`);
      const d = await r.json();
      setConnected(!!d.connected);
    } catch (e) {
      setConnected(false);
    }
  }, [userId]);

  const loadFolder = useCallback(async (folderId) => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/files?user_id=${userId}&folder_id=${encodeURIComponent(folderId)}&show_all=true`);
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.detail || 'Failed to load folder');
      setItems(d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { checkStatus(); }, [checkStatus]);
  useEffect(() => {
    if (connected) loadFolder(currentFolder.id);
  }, [connected, currentFolder.id, loadFolder]);

  const handleConnect = async () => {
    if (!userId) return;
    try {
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/connect?user_id=${userId}`);
      const d = await r.json();
      if (d.auth_url) window.location.href = d.auth_url;
    } catch (e) {
      alert(`Connect failed: ${e.message}`);
    }
  };

  const enterFolder = (item) => {
    setFolderStack(prev => [...prev, { id: item.id, name: item.name }]);
  };

  const goBack = () => {
    if (folderStack.length <= 1) return;
    setFolderStack(prev => prev.slice(0, -1));
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !userId) return;
    setUploading(true);
    try {
      let uploaded = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append('user_id', userId);
        fd.append('folder_id', currentFolder.id);
        fd.append('file', f);
        const r = await fetch(`${API_URL}/api/cloud-storage/drive/upload`, { method: 'POST', body: fd });
        if (r.ok) uploaded++;
        else console.warn('Upload failed for', f.name);
      }
      e.target.value = '';
      if (uploaded > 0) await loadFolder(currentFolder.id);
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !userId) return;
    setCreatingFolder(true);
    try {
      const fd = new FormData();
      fd.append('user_id', userId);
      fd.append('name', newFolderName.trim());
      fd.append('parent_id', currentFolder.id);
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/folders`, { method: 'POST', body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Folder create failed');
      }
      setNewFolderName('');
      setShowFolderInput(false);
      await loadFolder(currentFolder.id);
    } catch (err) {
      alert(`Folder create failed: ${err.message}`);
    } finally {
      setCreatingFolder(false);
    }
  };

  const openItem = async (item) => {
    if (item.type === 'folder' || item.mime_type === 'application/vnd.google-apps.folder') {
      enterFolder(item);
      return;
    }
    // For spreadsheets → open in-app editor
    if (isSpreadsheetMime(item.mime_type) || isSpreadsheetName(item.name)) {
      try {
        setEditorFile({ ...item, _loading: true });
        const r = await fetch(`${API_URL}/api/cloud-storage/drive/download/${item.id}?user_id=${userId}`);
        const d = await r.json();
        if (!r.ok || !d.success) throw new Error(d.detail || 'Download failed');
        // base64 → ArrayBuffer
        const binary = atob(d.content_base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        setEditorBytes(bytes);
        setEditorFile({ ...item, mime_type: d.mime_type, original_mime_type: d.original_mime_type });
        setEditorOpen(true);
      } catch (err) {
        alert(`Could not open file: ${err.message}`);
        setEditorFile(null);
      }
      return;
    }
    // Everything else → open in new tab via Drive web viewer
    window.open(`https://drive.google.com/file/d/${item.id}/view`, '_blank');
  };

  const saveEditedSpreadsheet = async (blob, mime) => {
    if (!editorFile) return;
    // Native Google Sheets cannot be patched directly with binary content —
    // would require Sheets API. So:
    //  - If original was a Google Sheet → upload as new XLSX in same folder
    //  - If original was XLSX/CSV → patch in place
    const isNativeGoogle = editorFile.original_mime_type === 'application/vnd.google-apps.spreadsheet';
    if (isNativeGoogle) {
      const fd = new FormData();
      fd.append('user_id', userId);
      fd.append('folder_id', currentFolder.id);
      const out = new File([blob], editorFile.name.replace(/(\.gsheet)?$/i, '') + '.xlsx', { type: mime });
      fd.append('file', out);
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/upload`, { method: 'POST', body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      alert('Saved as a new .xlsx file (the original was a native Google Sheet — Drive does not allow direct binary overwrites of those).');
    } else {
      const fd = new FormData();
      fd.append('user_id', userId);
      const fname = editorFile.name || 'spreadsheet.xlsx';
      const out = new File([blob], fname, { type: mime });
      fd.append('file', out);
      const r = await fetch(`${API_URL}/api/cloud-storage/drive/update/${editorFile.id}`, { method: 'PATCH', body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
    }
    await loadFolder(currentFolder.id);
  };

  if (connected === null) {
    return <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Checking Google Drive...</div>;
  }
  if (!connected) {
    return (
      <div className="p-8 sm:p-12 text-center" data-testid="drive-not-connected">
        <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-500 mb-4" />
        <h3 className="text-white text-lg font-semibold mb-2">Connect Google Drive</h3>
        <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
          Browse, upload, and edit spreadsheets directly from your Drive folders. Google Sheets, Excel, and CSV files open in a built-in editor.
        </p>
        <button
          onClick={handleConnect}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-2"
          data-testid="drive-connect-btn"
        >
          Connect Google Drive
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="drive-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-black/60 border border-white/10 rounded-lg">
        {folderStack.length > 1 && (
          <button
            onClick={goBack}
            className="p-1.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-md"
            data-testid="drive-back-btn"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        <div className="text-sm text-gray-200 font-medium flex-1 min-w-0 truncate">
          {folderStack.map((f, i) => (
            <span key={i}>
              {i > 0 && <span className="text-gray-500 mx-1">/</span>}
              {f.name}
            </span>
          ))}
        </div>
        <button
          onClick={() => loadFolder(currentFolder.id)}
          className="p-1.5 text-gray-300 hover:text-white hover:bg-white/10 rounded-md"
          title="Refresh"
          data-testid="drive-refresh-btn"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
        <button
          onClick={() => setShowFolderInput(s => !s)}
          className="px-3 py-1.5 text-sm text-white bg-white/10 hover:bg-white/15 border border-white/15 rounded-md inline-flex items-center gap-1.5"
          data-testid="drive-new-folder-btn"
        >
          <Folder size={14} /> New Folder
        </button>
        <label
          className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-500 rounded-md inline-flex items-center gap-1.5 cursor-pointer"
          data-testid="drive-upload-btn"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading…' : 'Upload'}
          <input type="file" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {showFolderInput && (
        <div className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded-lg">
          <input
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
            placeholder="Folder name"
            className="flex-1 px-3 py-1.5 text-sm bg-gray-900 border border-white/10 rounded-md text-white outline-none focus:border-blue-500"
            data-testid="drive-new-folder-input"
            autoFocus
          />
          <button
            onClick={handleCreateFolder}
            disabled={creatingFolder || !newFolderName.trim()}
            className="px-3 py-1.5 text-sm text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded-md"
            data-testid="drive-new-folder-create-btn"
          >
            {creatingFolder ? <Loader2 size={14} className="animate-spin" /> : 'Create'}
          </button>
          <button
            onClick={() => { setShowFolderInput(false); setNewFolderName(''); }}
            className="px-3 py-1.5 text-sm text-gray-300 hover:bg-white/10 rounded-md"
          >
            Cancel
          </button>
        </div>
      )}

      {/* List */}
      <div className="bg-black/40 border border-white/10 rounded-lg overflow-hidden">
        {error ? (
          <div className="p-6 text-red-400 text-sm" data-testid="drive-error">{error}</div>
        ) : loading ? (
          <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm" data-testid="drive-empty">This folder is empty.</div>
        ) : (
          <div className="divide-y divide-white/5" data-testid="drive-files-list">
            {items.map(item => {
              const Icon = fileIcon(item.mime_type, item.name);
              const isFolder = item.type === 'folder' || item.mime_type === 'application/vnd.google-apps.folder';
              return (
                <button
                  key={item.id}
                  onClick={() => openItem(item)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
                  data-testid={`drive-item-${item.id}`}
                >
                  <Icon size={18} className={isFolder ? 'text-blue-400 shrink-0' : 'text-gray-300 shrink-0'} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{item.name}</div>
                    <div className="text-gray-500 text-xs">
                      {isFolder ? 'Folder' : (
                        <>{item.mime_type?.split('/')[1] || 'file'}{item.size ? ` · ${formatBytes(item.size)}` : ''}</>
                      )}
                    </div>
                  </div>
                  {!isFolder && !isSpreadsheetMime(item.mime_type) && !isSpreadsheetName(item.name) && (
                    <ExternalLink size={14} className="text-gray-500" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <SpreadsheetEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditorBytes(null); setEditorFile(null); }}
        fileName={editorFile?.name}
        mimeType={editorFile?.mime_type}
        bytes={editorBytes}
        onSave={saveEditedSpreadsheet}
      />
    </div>
  );
};

export default GoogleDrivePanel;
